import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/Store';
import { RobotActions } from '../../utils/constants';
import usePrevious from './usePrevious';

const { STATE } = RobotActions;

interface ConnectionNotification {
  message: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Hook to manage connection status notifications.
 * Shows snackbar messages when robot connects or disconnects.
 *
 * Uses a ref to store the robot name while connected because Redux clears
 * robotType on disconnect before we can read the name for the notification.
 */
export const useConnectionNotification = (): ConnectionNotification => {
  const { t } = useTranslation();
  const { robotState } = useSelector((state: RootState) => state.robot);
  const { robotType } = useSelector((state: RootState) => state.robotData);

  const prevRobotState = usePrevious(robotState);
  const connectedRobotNameRef = useRef<string>('');

  const [message, setMessage] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Store robot name while connected (needed for disconnect message)
    const currentName = robotType?.name;
    if (currentName && currentName !== 'robotName' && robotState === STATE.CONNECTED) {
      connectedRobotNameRef.current = currentName;
    }

    // Skip if no state transition
    if (prevRobotState === undefined || prevRobotState === robotState) {
      return;
    }

    // Connected: show "Robot Name Connected"
    if (robotState === STATE.CONNECTED) {
      const robotName = currentName || connectedRobotNameRef.current;
      if (robotName && robotName !== 'robotName') {
        setMessage(`${robotName} ${t('snackbar.connected', 'Connected')}`);
        setOpen(true);
      }
    }
    // Disconnected: show "Robot Name Disconnected"
    else if (robotState === STATE.DISCONNECTED && connectedRobotNameRef.current) {
      setMessage(`${connectedRobotNameRef.current} ${t('snackbar.disconnected', 'Disconnected')}`);
      setOpen(true);
      connectedRobotNameRef.current = '';
    }
  }, [robotState, prevRobotState, robotType?.name, t]);

  return {
    message,
    open,
    onClose: () => setOpen(false),
  };
};
